import axios, { AxiosResponse } from 'axios';

import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { DEFAULT_API_ERROR_MESSAGE } from './constants.api';
import { PaginatedResponse } from './types.api';
import { v4 as uuidv4 } from 'uuid';
import { clearAuthData } from '@lucid/auth';

const AUTH_RETRY_MAX_DEFAULT = 1;
const AUTH_RETRY_TTL_MS = 10_000;
const AUTH_RETRY_HEADER = 'X-Auth-Retry-Max';
const authRetryTracker = new Map<string, { count: number; lastAt: number }>();

const getHeaderValue = (
  headers?: AxiosRequestConfig['headers'],
  name?: string
): string | undefined => {
  if (!headers || !name) return undefined;
  if (typeof (headers as { get?: (key: string) => string | undefined }).get === 'function') {
    return (headers as { get: (key: string) => string | undefined }).get(name) || undefined;
  }
  const headerValue = (headers as Record<string, unknown>)[name];
  return typeof headerValue === 'string' ? headerValue : undefined;
};

const getRequestKey = (config?: AxiosRequestConfig): string => {
  const method = config?.method ?? 'get';
  const baseUrl = config?.baseURL ?? '';
  const url = config?.url ?? '';
  return `${method.toLowerCase()}:${baseUrl}${url}`;
};

const shouldRedirectOnForbidden = (config?: AxiosRequestConfig): boolean => {
  const headerValue = getHeaderValue(config?.headers, AUTH_RETRY_HEADER);
  const retryMax = headerValue !== undefined ? Number(headerValue) : AUTH_RETRY_MAX_DEFAULT;
  const normalizedRetryMax = Number.isFinite(retryMax) ? retryMax : AUTH_RETRY_MAX_DEFAULT;

  const key = getRequestKey(config);
  const now = Date.now();
  const existing = authRetryTracker.get(key);

  if (!existing || now - existing.lastAt > AUTH_RETRY_TTL_MS) {
    authRetryTracker.set(key, { count: 1, lastAt: now });
    return normalizedRetryMax === 0;
  }

  const nextCount = existing.count + 1;
  authRetryTracker.set(key, { count: nextCount, lastAt: now });
  return nextCount > normalizedRetryMax;
};

const resetAuthRetry = (config?: AxiosRequestConfig): void => {
  const key = getRequestKey(config);
  if (authRetryTracker.has(key)) {
    authRetryTracker.delete(key);
  }
};

/**
 * Handles 401 Unauthorized responses by logging out the user and redirecting to login
 * with a returnTo parameter to bring them back after re-authentication
 */
const handle401Unauthorized = (): void => {
  // Clear all auth data and per-user identity cache
  clearAuthData();

  // Clear user identity from Zustand store
  const storedData = localStorage.getItem('theme-storage');
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      if (parsedData.state) {
        parsedData.state.userIdentity = null;
        localStorage.setItem('theme-storage', JSON.stringify(parsedData));
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Get current URL to return to after login
  const returnTo = window.location.pathname + window.location.search;
  const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}&sessionExpired=true`;

  // Redirect to login page
  window.location.href = loginUrl;
};

type ApiConfig<ResponseShape> = {
  extractData?: (response: ResponseShape) => unknown;
  extractError?: (data: unknown) => string | null;
  paginate?: (response: ResponseShape) => PaginatedResponse;
  axiosConfig?: AxiosRequestConfig;
};

/**
 * Recursively sanitize request data before sending to backend.
 * Converts empty/whitespace-only strings to null.
 */
const sanitizeRequestData = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;
  if (typeof data === 'string') return data.trim() || null;
  if (Array.isArray(data)) return data.map(sanitizeRequestData);
  if (typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [
        key,
        sanitizeRequestData(value),
      ])
    );
  }
  return data;
};

export function createApi<ResponseShape = unknown>(
  baseURL: string,
  config?: ApiConfig<ResponseShape>
) {
  const { extractData, extractError, paginate, axiosConfig } = config ?? {};

  const instance: AxiosInstance = axios.create({
    baseURL,
    ...axiosConfig,
  });

  instance.interceptors.request.use((config) => {
    config.headers.set('X-Correlation-Id', uuidv4());

    // Sanitize request body: trim strings and convert empty strings to null
    if (config.data && typeof config.data === 'object') {
      config.data = sanitizeRequestData(config.data);
    }

    return config;
  });

  // Response interceptor to handle 401 Unauthorized errors
  instance.interceptors.response.use(
    (response) => {
      resetAuthRetry(response.config);
      return response;
    },
    (error) => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401) {
          handle401Unauthorized();
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }

        if (status === 403 && shouldRedirectOnForbidden(error.config)) {
          // Skip redirect for /partners/me — handled by useUserIdentitySync
          // to avoid a loop (Auth0 session is valid but user has no backend account)
          const requestUrl = error.config?.url ?? '';
          if (!requestUrl.includes('/partners/me')) {
            handle401Unauthorized();
            return Promise.reject(new Error('Access forbidden. Please log in again.'));
          }
        }
      }
      return Promise.reject(error);
    }
  );

  async function apiCall<T>(
    axiosCall: (axiosConfig?: AxiosRequestConfig) => Promise<{ data: ResponseShape }>,
    options?: {
      overrides?: Partial<ApiConfig<ResponseShape>>;
      shouldPaginate?: boolean;
    }
  ): Promise<T> {
    try {
      const axiosConfig = options?.overrides?.axiosConfig;
      const response = await axiosCall(axiosConfig);
      const { overrides, shouldPaginate = false } = options || {};
      const dataExtractor =
        overrides?.extractData || extractData || ((response: ResponseShape) => response);

      if (shouldPaginate) {
        const paginateExtractor = overrides?.paginate || paginate;
        if (!paginateExtractor)
          throw new Error('Pagination requested but no paginate function provided');
        return paginateExtractor(response.data) as T;
      }

      return dataExtractor(response.data) as T;
    } catch (error) {
      throw handleError(error, options?.overrides);
    }
  }

  function handleError(error: unknown, overrides?: Partial<ApiConfig<ResponseShape>>): Error {
    if (axios.isAxiosError(error)) {
      console.error('HTTP Error:', error);

      const errorExtractor = overrides?.extractError || extractError;

      if (errorExtractor) {
        const applicationError = errorExtractor(error.response?.data);
        if (applicationError) {
          const err = new Error(applicationError) as Error & {
            status?: number;
            responseData?: unknown;
          };
          if (error.response?.status) err.status = error.response.status;
          if (error.response?.data !== undefined) err.responseData = error.response.data;
          return err;
        }
      }

      const responseData = error.response?.data as { error?: string } | undefined;
      const message = responseData?.error || error.message || DEFAULT_API_ERROR_MESSAGE;
      const err = new Error(message) as Error & { status?: number; responseData?: unknown };
      if (error.response?.status) err.status = error.response.status;
      if (error.response?.data !== undefined) err.responseData = error.response.data;
      return err;
    }

    return error instanceof Error ? error : new Error(DEFAULT_API_ERROR_MESSAGE);
  }

  return {
    instance,
    async get<TResponse>(url: string, overrides?: ApiConfig<ResponseShape>): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.get<ResponseShape>(url, axiosConfig), {
        overrides,
      });
    },

    async getPaginated<TResponse>(
      url: string,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<PaginatedResponse<TResponse>> {
      return apiCall((axiosConfig) => instance.get<ResponseShape>(url, axiosConfig), {
        overrides,
        shouldPaginate: true,
      });
    },

    async post<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.post<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async put<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.put<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async patch<TRequest, TResponse>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall((axiosConfig) => instance.patch<ResponseShape>(url, data, axiosConfig), {
        overrides,
      });
    },

    async delete<TRequest = void, TResponse = void>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<TResponse> {
      return apiCall(
        (axiosConfig) => instance.delete<ResponseShape>(url, { data, ...axiosConfig }),
        { overrides }
      );
    },

    async downloadFile<TRequest>(
      url: string,
      data?: TRequest,
      overrides?: ApiConfig<ResponseShape>
    ): Promise<AxiosResponse<Blob>> {
      try {
        const axiosConfig = overrides?.axiosConfig || {};
        return await instance.post<Blob>(url, data, { ...axiosConfig, responseType: 'blob' });
      } catch (error) {
        throw handleError(error, overrides);
      }
    },
  };
}

export function transformPaginated<TInput, TOutput>(
  paginatedData: PaginatedResponse<TInput>,
  transformer: (input: TInput) => TOutput
): PaginatedResponse<TOutput> {
  return {
    items: transformer(paginatedData.items),
    pagination: paginatedData.pagination,
  };
}
